import { faCarSide, faGifts } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import moment from "moment";
import * as React from "react";
import { useApolloClient } from "react-apollo";
import Helmet from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardImg,
  CardText,
  CardTitle,
  Col,
  Container,
  Jumbotron,
  Row
} from "reactstrap";
import config from "../../config";
import { USER_DEALER } from "../Dealers/UserDealerQuery";
import { GET_LOGGED_IN_USER, useLoggedInUser } from "../LoggedInUserQuery";
import { PROPOSAL_LIST } from "../Proposals/ProposalList/ProposalListQuery";
import { ActiveProposalStatus, ProposalSearchDate } from "../Proposals/types";
import { QUOTATION_LIST } from "../Quotations/QuotationList/QuotationListQuery";
import SceneLoadingSpinner from "../SceneLoadingSpinner";
import Image_1 from "./eurodrive-col-1.png";
import Image_2 from "./eurodrive-col-2.png";
import Image_3 from "./eurodrive-col-3.png";
import HomeSceneNotifications from "./HomeSceneNotifications";
import "./index.scss";
import { useDealerIAR } from "../../utils";
import { PermissionModuleNames } from "../Permissions/types";

const dashboardPromise =
  import("../AccountManagers/AccountManagerScene/UserAccountManagerTargetsTabContent");
const Dashboard = React.lazy(() => dashboardPromise);

const usePreloadedData = () => {
  const client = useApolloClient();

  React.useEffect(() => {
    // Preload the logged in user
    client.query({ query: GET_LOGGED_IN_USER });
    // Preload the user's dealer
    client.query({ query: USER_DEALER });
    // Preload the first page of proposals
    client.query({
      query: PROPOSAL_LIST,
      variables: {
        input: {
          fundedDealersOnly: false,
          hasDebitBackOnly: false,
          page: 1,
          pageSize: 10,
          status: ActiveProposalStatus,
          statisticsFilterByDate: true,
          filterByDate: ProposalSearchDate.PAID_OUT_DATE
        }
      }
    });
    // Preload the first page of quotations
    client.query({
      query: QUOTATION_LIST,
      variables: { input: { page: 1, pageSize: 10 } }
    });
  });
};

const HomeScene = () => {
  usePreloadedData();
  const {
    checkCanCreate,
    loggedInUser,
    isAccountManager,
    isRegionalSalesManager
  } = useLoggedInUser();

  const isDealerIAR = useDealerIAR(loggedInUser);

  if (!loggedInUser) {
    return null;
  }

  const isSales = isAccountManager || isRegionalSalesManager;

  return (
    <>
      <Helmet title="Home | Eurodrive" />
      <Container className="home-scene">
        {moment().isSameOrBefore(moment("2020-01-02", "YYYY-MM-DD")) ? (
          <Alert color="info">
            <h2>
              <FontAwesomeIcon icon={faGifts} className="mr-3" />
              Eurodrive festive opening times
            </h2>
            <ul className="mt-3 mb-0">
              <li>Christmas Eve: 9am – 1pm</li>
              <li>
                Christmas Day: <b>Closed</b>
              </li>
              <li>
                Boxing Day: <b>Closed</b>
              </li>
              <li>New Year's Eve: 9am – 3pm</li>
              <li>
                New Year's Day: <b>Closed</b>
              </li>
            </ul>
          </Alert>
        ) : null}

        {isSales && (
          <Alert className="d-block d-md-none" fade={false} color="warning">
            <FontAwesomeIcon icon={faCarSide} className="mr-2" />
            Do not use this site while driving
          </Alert>
        )}
        <Jumbotron
          className={classnames("home-jumbotron mb-3", {
            "d-none": isSales,
            "d-md-block": isSales
          })}
        >
          <Row>
            <Col md="8">
              <div className="homepage-text">
                <h1>Welcome to the Eurodrive Motor Finance Dealer Portal</h1>
                <h2>What would you like to do today?</h2>
                <div className="lead mt-4 d-flex flex-column flex-sm-row">
                  {checkCanCreate(PermissionModuleNames.Quotations) &&
                  !isDealerIAR ? (
                    <RouterLink
                      to="quotations/create"
                      className="btn btn-primary btn-lg mr-sm-3 mb-3"
                    >
                      New Finance Quotation
                    </RouterLink>
                  ) : null}
                  <RouterLink
                    to="proposals"
                    className="btn btn-secondary btn-lg mb-3"
                  >
                    Find a Proposal
                  </RouterLink>
                </div>
              </div>
            </Col>
          </Row>
        </Jumbotron>
        {isSales ? (
          <React.Suspense fallback={<SceneLoadingSpinner />}>
            <Row className="mb-3">
              <Col lg={4}>
                <h3 style={{ color: "#ff6b3a" }} className="mb-3">
                  Today's activity
                </h3>
                <HomeSceneNotifications />
              </Col>
              <Col lg={8}>
                <Dashboard />
              </Col>
            </Row>
          </React.Suspense>
        ) : null}
        {config.PROMO_URL && config.PROMO_IMAGE ? (
          <Row className="mb-3">
            <Col md={12}>
              <Card>
                <a href={`${config.PROMO_URL}`}>
                  <CardImg top={true} src={`${config.PROMO_IMAGE}`} />
                </a>
              </Card>
            </Col>
          </Row>
        ) : null}
        <Row className="mb-3">
          <Col md={4}>
            <Card>
              <CardImg top={true} src={Image_1} />
              <CardBody>
                <CardTitle>The Eurodrive Motor Finance Dealer Club</CardTitle>
                <CardText>
                  A dealer-first finance ecosystem designed to increase revenue
                  and close more deals.
                </CardText>
                <CardFooter>
                  <a
                    href="https://eurodrivefinance.co.uk/pages/the-eurodrive-motor-finance-dealer-club"
                    className="btn btn-secondary"
                  >
                    Join The Dealer Club
                  </a>
                </CardFooter>
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <CardImg top={true} src={Image_2} />
              <CardBody>
                <CardTitle>Sales Resources</CardTitle>
                <CardText>
                  Dealer-focused insights designed to support stronger finance
                  conversations and better outcomes
                </CardText>
                <CardFooter>
                  <a
                    href="https://eurodrivefinance.co.uk/blogs/sales-resources"
                    className="btn btn-secondary align-self-end"
                  >
                    View Sales Resources
                  </a>
                </CardFooter>
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <CardImg top={true} src={Image_3} />
              <CardBody>
                <CardTitle>FCA Compliance</CardTitle>
                <CardText>
                  Eurodrive Finance has blazed a trail in helping motor dealers
                  understand the effects and implications of new regulation.
                </CardText>
                <CardFooter>
                  <a
                    href="https://eurodrivefinance.co.uk/pages/fca-regulation"
                    className="btn btn-secondary"
                  >
                    Download guide
                  </a>
                </CardFooter>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <div>
          <small className="text-muted">&copy; Eurodrive Motor Finance </small>
          <small className="float-right">
            <RouterLink to="/releasenotes">Release notes</RouterLink>
          </small>
        </div>
      </Container>
    </>
  );
};

export default HomeScene;
